import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import apiV2 from '../../services/apiV2';
import { cn } from '../../lib/cn';
import { useOptionalAuth } from '../../hooks/useAuth';
import { TeacherClassSelector, useTeacherWorkspace } from './TeacherWorkspace';
import { teacherClassLink } from '../../services/teacherSection';
import { Badge } from '../ui/badge';
import { TeacherNavigation } from './TeacherNavigation';

export interface AppShellNavItem {
  id: string;
  label: string;
  to: string;
  active?: boolean;
  group?: string;
  badge?: number | null;
}

export interface AppShellProps {
  role: 'teacher' | 'student';
  studentSectionId?: number;
  tone?: 'default' | 'elevate';
  navItems?: AppShellNavItem[];
  actions?: React.ReactNode;
  title?: string;
  className?: string;
  children: React.ReactNode;
}

export const AppShell: React.FC<AppShellProps> = ({
  role,
  studentSectionId,
  tone = 'default',
  navItems = [],
  actions,
  title = 'Hibbardsclass',
  className,
  children,
}) => {
  const navigate = useNavigate();
  const workspace = useTeacherWorkspace();
  const menuButton = useRef<HTMLButtonElement>(null);
  const auth = useOptionalAuth();
  const user = auth?.user;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);
  const [learningClasses, setLearningClasses] = useState<any[]>([]);
  useEffect(() => {
    if (role !== 'student') return;
    let current = true;
    apiV2.get('/my/learning-configurations/').then(({data}) => { if (current) setLearningClasses(data.classes || []); }).catch(() => {});
    return () => { current = false; };
  }, [role]);
  let savedSection = 0;
  try { savedSection = Number(sessionStorage.getItem('student-classroom-section')); } catch { /* Preference only. */ }
  const classroomSection = location.pathname.match(/^\/v2\/student\/classroom\/(\d+)/)?.[1];
  const navSection = navItems.map(item => Number(new URLSearchParams(item.to.split('?')[1]).get('section'))).find(Boolean);
  const sectionId = studentSectionId || Number(classroomSection) || Number(new URLSearchParams(location.search).get('section')) || navSection || savedSection;
  const learning = learningClasses.find(c => c.id === sectionId) || (!sectionId ? learningClasses[0] : undefined);
  const dailyChecklist = role === 'student' && learning?.daily_checklist === true;
  const dailySection = learning?.id || sectionId;
  const [dailyStatus, setDailyStatus] = useState('');
  useEffect(() => {
    if (!dailyChecklist || !dailySection) { setDailyStatus(''); return; }
    let current = true;
    setDailyStatus('');
    const load = () => apiV2.get('/goals/my/today/', { params: { section_id: dailySection } }).then(({data}) => {
      if (current) setDailyStatus(data.status || '');
    }).catch(() => { if (current) setDailyStatus('unavailable'); });
    void load();
    window.addEventListener('daily-checklist-saved', load);
    return () => { current = false; window.removeEventListener('daily-checklist-saved', load); };
  }, [dailyChecklist, dailySection]);
  const currentTab = new URLSearchParams(location.search).get('tab');
  const dailyNav: AppShellNavItem[] = [
    { id: 'today', label: 'Today', to: `/v2/student?section=${dailySection}`, active: location.pathname === '/v2/student' && (!currentTab || currentTab === 'today') },
    { id: 'work', label: 'My work', to: `/v2/student?tab=workspace&section=${dailySection}`, active: ['workspace', 'work'].includes(currentTab || '') || /units|classroom|assignments|runs/.test(location.pathname) },
    { id: 'goals', label: 'Goals & check-ins', to: `/v2/student?tab=goal-home&section=${dailySection}`, active: ['goal-home', 'goals'].includes(currentTab || '') || (/coaching|\/reviews\//.test(location.pathname)) },
    { id: 'progress', label: 'Progress', to: `/v2/student?tab=progress&section=${dailySection}`, active: ['progress', 'portfolio'].includes(currentTab || '') || /standards|work-review/.test(location.pathname) },
  ];
  const studentNav = navItems.filter(item => (item.id !== 'standards' || learning?.standards === true) && (!learning ||
    (!['coaching', 'goals'].includes(item.id) || learning.coaching) && (item.id !== 'pathways' || learning.projects || learning.courses || learning.roadmap))).map(item => ({
      ...item,
      label: item.id === 'pathways' && learning ? (learning.roadmap || learning.projects && learning.courses ? 'My learning' : learning.courses ? 'My courses' : 'My projects') : item.label,
      to: learning && !new URLSearchParams(item.to.split('?')[1]).has('section') ? `${item.to}${item.to.includes('?') ? '&' : '?'}section=${learning.id}` : item.to,
    }));

  return (
    <div className={`min-h-screen bg-bg text-text ${role === 'teacher' ? 'teacher-workspace' : ''} ${tone === 'elevate' || role === 'student' ? 'elevate-workspace-theme' : ''}`} onKeyDown={event => { if (event.key === 'Escape' && menuOpen) { setMenuOpen(false); menuButton.current?.focus(); } }} onClickCapture={event => {
      if (role !== 'teacher' || !workspace) return;
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor || anchor.target === '_blank' || event.ctrlKey || event.metaKey || event.shiftKey) return;
      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin || !url.pathname.startsWith('/v2/teacher')) return;
      const next = teacherClassLink(url.pathname + url.search + url.hash, workspace.selected);
      anchor.setAttribute('href', next);
      event.preventDefault(); event.stopPropagation();
      workspace.requestLeave(() => navigate(next));
    }}>
      <header className="sticky top-0 z-40 border-b border-border bg-surface/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-muted">Hibbardsclass</p>
              <div className="mt-1 flex items-center gap-2">
                {role === 'teacher' ? <p className="text-lg font-semibold text-text">Teacher workspace</p> : <h1 className="text-lg font-semibold text-text">{title}</h1>}
                <Badge variant={role === 'teacher' ? 'default' : 'muted'}>{role}</Badge>
              </div>
            </div>
            {role === 'teacher' && <TeacherClassSelector />}
            {dailyChecklist && dailyStatus && dailyStatus !== 'not_checked_in' && <Link className="btn-primary" to={`/v2/student?section=${dailySection}&view=wrap`}>{dailyStatus === 'checked_out' ? 'View my wrap-up' : 'Finish my day'}</Link>}
            {role === 'student' && !dailyChecklist && navItems.length > 0 && <button ref={menuButton} type="button" aria-expanded={menuOpen} aria-controls="student-menu" onClick={() => setMenuOpen(value => !value)} className="btn-secondary">My learning</button>}
            {(actions || user) ? (
              <div className="flex flex-wrap items-center gap-3">
                {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
                {user ? (
                  <div className="flex items-center gap-3 border-l border-border pl-3">
                    <div className="text-right text-xs">
                      <p className="font-medium text-text">{user.first_name} {user.last_name}</p>
                      <p className="capitalize text-muted">{user.role}</p>
                    </div>
                    <button
                      type="button"
                      className="rounded-md border border-border bg-surface px-3 py-1.5 text-sm font-medium text-text hover:bg-surface-alt"
                      onClick={auth?.logout}
                    >
                      Sign out
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {role !== 'teacher' && navItems.length > 0 && (
            <nav id="student-menu" hidden={!menuOpen && !dailyChecklist} className={menuOpen || dailyChecklist ? 'flex flex-wrap items-center gap-2' : 'hidden'} aria-label="Student navigation">
              {(dailyChecklist ? dailyNav : studentNav).map((item) => (
                <Link
                  key={item.id}
                  to={item.to}
                  aria-current={item.active ? 'page' : undefined}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    item.active
                      ? 'bg-brand text-brand-foreground shadow-soft'
                      : 'text-muted hover:bg-surface-alt hover:text-text',
                  )}
                >
                  <span>{item.label}</span>
                  {typeof item.badge === 'number' && item.badge > 0 ? (
                    <span className={cn(
                      'ml-2 rounded-full px-2 py-0.5 text-xs font-semibold',
                      item.active ? 'bg-white/20 text-brand-foreground' : 'bg-surface-alt text-text',
                    )}>
                      {item.badge}
                    </span>
                  ) : null}
                </Link>
              ))}
            </nav>
          )}
        </div>
      </header>
      {role === 'teacher' && navItems.length > 0 ? <div className="mx-auto max-w-screen-2xl lg:grid lg:grid-cols-[230px_minmax(0,1fr)]">
        <aside className="p-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:self-start">
          <button ref={menuButton} type="button" aria-expanded={menuOpen} aria-controls="teacher-menu" onClick={() => setMenuOpen(value => !value)} className="mb-2 w-full rounded border border-border bg-surface p-3 text-left font-semibold lg:hidden">Teacher menu</button>
          <div id="teacher-menu" className={menuOpen ? 'block' : 'hidden lg:block'}><TeacherNavigation items={navItems} /></div>
        </aside>
        <main className={cn('min-w-0 px-4 py-6 sm:px-6', className)}>
          {title !== 'Class Overview' && <nav aria-label="Breadcrumb" className="mb-4 flex flex-wrap gap-2 text-xs text-muted"><Link to={teacherClassLink('/v2/teacher', workspace?.selected)} className="underline">Overview</Link><span aria-hidden="true">/</span>{navItems.find(item => item.active)?.group !== title && <><span>{navItems.find(item => item.active)?.group || 'Teacher'}</span><span aria-hidden="true">/</span></>}<span aria-current="page">{title}</span></nav>}
          {children}
        </main>
      </div> : <main className={cn('mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8', className)}>{children}</main>}
    </div>
  );
};

export interface OptionalAppShellProps extends AppShellProps {
  enabled: boolean;
}

export const OptionalAppShell: React.FC<OptionalAppShellProps> = ({
  enabled,
  children,
  ...appShellProps
}) => (
  enabled ? <AppShell {...appShellProps}>{children}</AppShell> : <>{children}</>
);
